import { useMemo, useState } from 'react';
import {
    ArchiveBoxIcon,
    ArrowPathIcon,
    BoltIcon,
    BuildingOffice2Icon,
    ChatBubbleLeftRightIcon,
    ChevronDownIcon,
    DocumentTextIcon,
    LinkIcon,
    LinkSlashIcon,
    PaperAirplaneIcon,
    PencilSquareIcon,
    PlayCircleIcon,
    ShoppingCartIcon,
    StopCircleIcon,
    TrashIcon,
    UserPlusIcon
} from '@heroicons/react/24/outline';
import { CATEGORIES } from './categories';
import { RdiLandingPage } from './rdi/RdiLandingPage';
import { SdiLandingPage } from './sdi/SdiLandingPage';
import { DxReportLandingPage } from './dx-report/DxReportLandingPage';
import { RefinementLandingPage } from './vca/refinement/RefinementLandingPage';
import { RetentionLandingPage } from './vca/retention/RetentionLandingPage';
import { OpenVasNowAction } from './vca/OpenVasNowAction';
import { ArchiveCaseAction } from './admin/ArchiveCaseAction';
import { DeletePatientAction } from './admin/DeletePatientAction';
import { EditPatientLanding } from './admin/EditPatientLanding';
import { EndTreatmentAction } from './admin/EndTreatmentAction';
import { MovePracticeLanding } from './admin/MovePracticeLanding';
import { OrderOrthopulseAction } from './admin/OrderOrthopulseAction';
import { SparkCaseLinkAction } from './admin/SparkCaseLinkAction';
import { CreateDmPatientLanding } from './dm/CreateDmPatientLanding';
import { SendActivationLinkLanding } from './dm/SendActivationLinkLanding';
import { ManageMonitoringLanding } from './dm/ManageMonitoringLanding';
import { DmNotesLanding } from './dm/DmNotesLanding';
import { DmPatientMessagesTab } from './dm/DmPatientMessagesTab';
import { UnlinkDmPatientLanding } from './dm/UnlinkDmPatientLanding';
import { LinkExistingDmPatientLanding } from './dm/LinkExistingDmPatientLanding';
import { useUser } from '../../../providers/UserProvider';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { useManufacturerStatus } from '../../../hooks/useManufacturerStatus';
import { isSparkManufacturer as checkIsSparkManufacturer } from '../../../utils/manufacturer';
import { ActionCard } from './ActionCard';
import { DmLinkStatusChip, DmMonitoringStatusChip, DmAppActivationChip } from './dmStatusChips';
import type { ActionCategoryId, ActionConfig } from './types';

interface ActionsTabProps {
    patientId: string;
    patientName: string;
    /** Raw Monday "Orthopulse Status" value — forwarded to RDI/SDI wizards. */
    orthopulseStatus?: string | null;
    /** Called when an action modifies patient data (e.g. new file uploaded). */
    onDataChange?: () => void;
    /** Action id to open on mount (e.g. from a `?action=` deep-link). */
    initialActionId?: string | null;
}

const ACTIONS: ActionConfig[] = [
    {
        id: 'sdi',
        categoryId: 'clinical',
        label: 'Create SDI',
        icon: DocumentTextIcon,
        component: SdiLandingPage
    },
    {
        id: 'rdi',
        categoryId: 'clinical',
        label: 'Create RDI',
        icon: DocumentTextIcon,
        component: RdiLandingPage
    },
    {
        id: 'dx_report',
        categoryId: 'clinical',
        label: 'Create Dx',
        icon: DocumentTextIcon,
        component: DxReportLandingPage
    },
    {
        id: 'trigger_refinement',
        categoryId: 'vca',
        label: 'Trigger refinement',
        icon: ArrowPathIcon,
        component: RefinementLandingPage
    },
    {
        id: 'trigger_retention',
        categoryId: 'vca',
        label: 'Trigger retention',
        icon: ArrowPathIcon,
        component: RetentionLandingPage
    },
    {
        id: 'open_vas_now',
        categoryId: 'vca',
        label: 'Add VAs',
        icon: BoltIcon,
        component: OpenVasNowAction,
        opensModal: true
    },
    {
        id: 'dm_create_patient',
        categoryId: 'dm',
        label: 'Create DM patient',
        icon: UserPlusIcon,
        component: CreateDmPatientLanding,
        requiresDmNotLinked: true
    },
    {
        id: 'dm_link_existing',
        categoryId: 'dm',
        label: 'Link to existing DM patient',
        icon: LinkIcon,
        component: LinkExistingDmPatientLanding,
        requiresDmNotLinked: true
    },
    {
        id: 'dm_send_activation_link',
        categoryId: 'dm',
        label: 'Send activation link',
        icon: PaperAirplaneIcon,
        component: SendActivationLinkLanding,
        requiresDmLink: true
    },
    {
        id: 'dm_manage_monitoring',
        categoryId: 'dm',
        label: 'Manage monitoring',
        icon: PlayCircleIcon,
        component: ManageMonitoringLanding,
        requiresDmLink: true,
        opensModal: true
    },
    {
        id: 'dm_notes',
        categoryId: 'dm',
        label: 'Edit DM notes',
        icon: PencilSquareIcon,
        component: DmNotesLanding,
        requiresDmLink: true
    },
    {
        id: 'dm_messages',
        categoryId: 'dm',
        label: 'Patient messages',
        icon: ChatBubbleLeftRightIcon,
        component: DmPatientMessagesTab,
        requiresDmLink: true
    },
    {
        id: 'dm_unlink',
        categoryId: 'dm',
        label: 'Unlink DM',
        icon: LinkSlashIcon,
        component: UnlinkDmPatientLanding,
        requiresDmLink: true
    },
    {
        id: 'edit_patient',
        categoryId: 'admin',
        label: 'Edit patient',
        icon: PencilSquareIcon,
        component: EditPatientLanding
    },
    {
        id: 'spark_link_case',
        categoryId: 'admin',
        label: 'Link / unlink Spark case',
        icon: LinkIcon,
        component: SparkCaseLinkAction
    },
    {
        id: 'end_treatment',
        categoryId: 'admin',
        label: 'End treatment',
        icon: StopCircleIcon,
        component: EndTreatmentAction
    },
    {
        id: 'archive_case',
        categoryId: 'admin',
        label: 'Archive case',
        icon: ArchiveBoxIcon,
        component: ArchiveCaseAction
    },
    {
        id: 'move_practice',
        categoryId: 'admin',
        label: 'Move to another practice',
        icon: BuildingOffice2Icon,
        component: MovePracticeLanding
    },
    {
        id: 'order_orthopulse',
        categoryId: 'admin',
        label: 'Order OrthoPulse',
        icon: ShoppingCartIcon,
        component: OrderOrthopulseAction
    },
    {
        id: 'delete_patient',
        categoryId: 'admin',
        label: 'Delete patient',
        icon: TrashIcon,
        component: DeletePatientAction
    }
];

function ActionsTab({
    patientId,
    patientName,
    orthopulseStatus,
    onDataChange,
    initialActionId
}: ActionsTabProps) {
    const { user } = useUser();
    const isAdmin = user?.database.data.isAdmin ?? false;
    const { data: patientCard, isLoading: isPatientCardLoading } = usePatientCard(patientId);
    const dmId = patientCard?.monitoring?.dmId ?? null;
    const monitoringStatus = patientCard?.monitoring?.monitoringStatus ?? null;
    const dmAppActivationStatus = patientCard?.monitoring?.dmAppActivationStatus ?? null;
    const isDmLinked = dmId != null;
    const isSparkLinked = (patientCard?.treatment?.manufacturerCaseId ?? null) != null;
    const isSparkManufacturer = checkIsSparkManufacturer(
        patientCard?.treatment?.manufacturerSlug ?? null
    );
    const hasPracticeSparkCreds = (patientCard?.practice?.sparkAcct ?? '').trim() !== '';
    // isSparkLinked must independently short-circuit this pre-filter: a case can stay
    // linked as Spark after the manufacturer or practice creds change, so gating solely
    // on isSparkManufacturer/hasPracticeSparkCreds would make the unlink action disappear.
    const { automationReady: canLinkSparkCase } = useManufacturerStatus(patientId, {
        enabled: (isSparkManufacturer && hasPracticeSparkCreds) || isSparkLinked
    });
    // A case linked as Spark but later switched to another Aligner Preference (or
    // whose practice lost automation readiness) must still expose this action so
    // ops can unlink the stale case — `unlinkSparkCaseService` doesn't gate on
    // manufacturer or automationReady, only `canLinkSparkCase` does.
    const canOpenSparkAction = canLinkSparkCase || isSparkLinked;
    const normalizedMonitoringStatus = (monitoringStatus ?? '').toLowerCase();
    const isMonitoringActive =
        normalizedMonitoringStatus === 'started' ||
        normalizedMonitoringStatus === 'resumed' ||
        normalizedMonitoringStatus === 'paused';

    const [selectedActionId, setSelectedActionId] = useState<string | null>(
        () => initialActionId ?? null
    );

    const [expandedCategories, setExpandedCategories] = useState<Set<ActionCategoryId>>(
        () => new Set(CATEGORIES.map((c) => c.id))
    );

    const toggleCategory = (id: ActionCategoryId) => {
        setExpandedCategories((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const activeAction = useMemo(() => {
        const a = ACTIONS.find((x) => x.id === selectedActionId) ?? null;
        if (a && a.categoryId === 'admin' && !isAdmin) return null;
        // Defensive: don't render a landing page for an action whose gating
        // conditions aren't met (the cards are also disabled in the grid).
        if (a && a.requiresDmLink && !isDmLinked) return null;
        if (a && a.requiresDmNotLinked && isDmLinked) return null;
        if (a && a.requiresMonitoring && !isMonitoringActive) return null;
        if (a && a.id === 'spark_link_case' && !canOpenSparkAction) return null;
        return a;
    }, [selectedActionId, isAdmin, isDmLinked, isMonitoringActive, canOpenSparkAction]);

    const sortedCategories = useMemo(
        () =>
            [...CATEGORIES]
                .filter((c) => isAdmin || c.id !== 'admin')
                .sort((a, b) => a.order - b.order),
        [isAdmin]
    );

    const actionsByCategory = useMemo(() => {
        const map = new Map<ActionCategoryId, ActionConfig[]>();
        for (const category of CATEGORIES) {
            map.set(category.id, []);
        }
        for (const action of ACTIONS) {
            if (action.categoryId === 'admin' && !isAdmin) continue;
            if (action.id === 'spark_link_case' && !canOpenSparkAction) continue;
            map.get(action.categoryId)?.push(action);
        }
        return map;
    }, [isAdmin, canOpenSparkAction]);

    const runner = activeAction && (
        <activeAction.component
            patientId={patientId}
            patientName={patientName}
            orthopulseStatus={orthopulseStatus}
            onBack={() => setSelectedActionId(null)}
            onDataChange={onDataChange}
        />
    );
    if (activeAction && !activeAction.opensModal) return runner;

    return (
        <div className="flex flex-col flex-1 min-h-0 overflow-auto">
            {runner}
            <div className="flex flex-col p-6 gap-3">
                {sortedCategories.map((category) => {
                    const actions = actionsByCategory.get(category.id) ?? [];
                    const isExpanded = expandedCategories.has(category.id);
                    return (
                        <div
                            key={category.id}
                            className="rounded-xl border border-gray-200 bg-white overflow-hidden"
                        >
                            <button
                                onClick={() => toggleCategory(category.id)}
                                className={`w-full flex items-center justify-between px-4 py-3 ${
                                    isExpanded ? 'border-b border-gray-100' : ''
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-semibold text-gray-900">
                                        {category.label}
                                    </h4>
                                    {category.id === 'dm' && (
                                        <>
                                            <DmLinkStatusChip
                                                isLoading={isPatientCardLoading}
                                                dmId={dmId}
                                            />
                                            {dmId != null && (
                                                <DmMonitoringStatusChip status={monitoringStatus} />
                                            )}
                                            {dmId != null && (
                                                <DmAppActivationChip
                                                    status={dmAppActivationStatus}
                                                />
                                            )}
                                        </>
                                    )}
                                </div>
                                <ChevronDownIcon
                                    className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${
                                        isExpanded ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>
                            <div
                                className={`grid transition-all duration-300 ease-out ${
                                    isExpanded
                                        ? 'grid-rows-[1fr] opacity-100'
                                        : 'grid-rows-[0fr] opacity-0'
                                }`}
                            >
                                <div className="overflow-hidden">
                                    <div className="p-4">
                                        {actions.length > 0 ? (
                                            <div className="flex flex-wrap gap-3">
                                                {actions.map((action) => {
                                                    const needsLink =
                                                        action.requiresDmLink === true &&
                                                        !isDmLinked;
                                                    const needsUnlink =
                                                        action.requiresDmNotLinked === true &&
                                                        isDmLinked;
                                                    const needsMonitoring =
                                                        action.requiresMonitoring === true &&
                                                        isDmLinked &&
                                                        !isMonitoringActive;
                                                    const isDisabled =
                                                        needsLink || needsUnlink || needsMonitoring;
                                                    const disabledReason = needsLink
                                                        ? 'Patient must be created in Dental Monitoring first.'
                                                        : needsUnlink
                                                          ? 'Patient is already linked to Dental Monitoring. Unlink first if you want to re-link.'
                                                          : needsMonitoring
                                                            ? 'Monitoring must be started before changing this. Use "Manage monitoring" to start it.'
                                                            : undefined;
                                                    // The Spark action links or unlinks depending on the
                                                    // patient's current state — reflect that in the card.
                                                    const isSparkAction =
                                                        action.id === 'spark_link_case';
                                                    const label =
                                                        isSparkAction && isSparkLinked
                                                            ? 'Unlink Manufacturer case'
                                                            : isSparkAction
                                                              ? 'Link Manufacturer case'
                                                              : action.label;
                                                    const icon =
                                                        isSparkAction && isSparkLinked
                                                            ? LinkSlashIcon
                                                            : action.icon;
                                                    return (
                                                        <ActionCard
                                                            key={action.id}
                                                            icon={icon}
                                                            label={label}
                                                            disabled={isDisabled}
                                                            disabledReason={disabledReason}
                                                            onSelect={() => {
                                                                if (isDisabled) return;
                                                                setSelectedActionId(action.id);
                                                            }}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-400 italic">
                                                No actions in this category yet.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export { ActionsTab };
